import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import L from 'leaflet';
import type { LeafletEventHandlerFnMap } from 'leaflet';
import { CircleMarker, Marker as LeafletMarker, Polygon, Polyline, useMap } from 'react-leaflet';
import { v4 as uuidv4 } from 'uuid';

import {
  addOverlayInImage,
  groupedUndo,
  labelPositionsSelector,
  moveLabelInImage,
  overlaysSelector,
  selectedOverlayIdSelector,
  selectedOverlayIdsSelector,
  setSelectedOverlayId,
  setSelectedOverlayIds,
  updateOverlayInImage,
  useAppDispatch,
  useAppSelector,
} from '../../../../store';
import { appendSelectionPoint, toSelectionPoint } from '../../../../utils/labels';
import { OverlayPoint } from '../../../../utils/overlays';
import rootElement from '../../../../rootElement';
import eventBus from '../../../../eventBus';
import { MapEventHandler } from '../../../Map/MapEventHandler';
import { EditOverlay } from '../../Sidebar/Edit/EditOverlay';
import type { OverlayStylePreview } from '.';

const MIN_POINTS = 3;
const PREVIEW_STYLE = { color: '#168c94', weight: 2, dashArray: '6 4' };

const vertexIcon = L.divIcon({ className: 'overlay-vertex-handle', iconSize: [12, 12], iconAnchor: [6, 6] });
const midpointIcon = L.divIcon({ className: 'overlay-midpoint-handle', iconSize: [10, 10], iconAnchor: [5, 5] });
// Four arrows, a bit up and to the right of the overlay top-right corner (its position)
const moveIcon = L.divIcon({
  className: 'overlay-move-handle',
  html: '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1 5.5 3.5h1.75v3.75H3.5V5.5L1 8l2.5 2.5V8.75h3.75v3.75H5.5L8 15l2.5-2.5H8.75V8.75h3.75v1.75L15 8l-2.5-2.5v1.75H8.75V3.5h1.75z"/></svg>',
  iconSize: [22, 22],
  iconAnchor: [-6, 28],
});

// Top-right corner of the box around some points
const topRightCorner = (points: OverlayPoint[]) => ({
  x: Math.max(...points.map((point) => point.x)),
  y: Math.max(...points.map((point) => point.y)),
});

const translatePoints = (points: OverlayPoint[], dx: number, dy: number) => (
  points.map((point) => ({ x: point.x + dx, y: point.y + dy }))
);

const toPositions = (points: OverlayPoint[]) => points.map((point) => [point.x, point.y] as [number, number]);

interface OverlayEditingProps {
  onStylePreviewChange: (preview: OverlayStylePreview | null) => void;
  // Where the label not created yet of the selected overlay was dragged to
  newLabelPosition?: OverlayPoint | null;
}

// Draw new overlays, and reshape/edit the selected one
export function OverlayEditing({ onStylePreviewChange, newLabelPosition }: OverlayEditingProps) {
  const dispatch = useAppDispatch();
  const map = useMap();
  const overlays = useAppSelector(overlaysSelector);
  const selectedOverlayId = useAppSelector(selectedOverlayIdSelector);
  const selectedOverlayIds = useAppSelector(selectedOverlayIdsSelector);
  const labelPositions = useAppSelector(labelPositionsSelector);
  const selectedOverlay = overlays.find((overlay) => overlay.id === selectedOverlayId) ?? null;
  // Edited along with the selected one
  const otherSelectedOverlays = useMemo(
    () => overlays.filter((overlay) => overlay.id !== selectedOverlayId && selectedOverlayIds.includes(overlay.id)),
    [overlays, selectedOverlayId, selectedOverlayIds],
  );
  const mapContainer = map.getContainer();

  const [drawPoints, setDrawPoints] = useState<OverlayPoint[]>([]);
  const [cursor, setCursor] = useState<OverlayPoint | null>(null);
  const drawPointsRef = useRef<OverlayPoint[]>([]);
  // Points of the selected overlay, updated live while dragging handles
  const [editPoints, setEditPoints] = useState<OverlayPoint[] | null>(null);

  useEffect(() => {
    setEditPoints(selectedOverlay ? selectedOverlay.points : null);
  }, [selectedOverlay]);

  const setDrawing = useCallback((points: OverlayPoint[]) => {
    drawPointsRef.current = points;
    setDrawPoints(points);
    if (points.length === 0) setCursor(null);
  }, []);

  const finishDrawing = useCallback(() => {
    const points = drawPointsRef.current;
    if (points.length < MIN_POINTS) return;

    const id = uuidv4();
    dispatch(addOverlayInImage({
      id,
      points,
      labelId: null,
      spaceId: null,
    }));
    dispatch(setSelectedOverlayId(id));
    setDrawing([]);
  }, [dispatch, setDrawing]);

  useEffect(() => {
    mapContainer.classList.add('overlay-editing');
    return () => mapContainer.classList.remove('overlay-editing');
  }, [mapContainer]);

  // Polygons can't be clicked while drawing, so clicks inside them add points
  useEffect(() => {
    mapContainer.classList.toggle('overlay-drawing', drawPoints.length > 0);
    return () => mapContainer.classList.remove('overlay-drawing');
  }, [drawPoints.length, mapContainer]);

  useEffect(() => {
    map.doubleClickZoom.disable();
    return () => {
      map.doubleClickZoom.enable();
    };
  }, [map]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLElement && event.target.closest('input, textarea, select, button')) return;
      if (event.key === 'Escape') {
        if (drawPointsRef.current.length > 0) {
          setDrawing([]);
        } else {
          dispatch(setSelectedOverlayId(null));
        }
      } else if (event.key === 'Enter' && drawPointsRef.current.length >= MIN_POINTS) {
        event.preventDefault();
        finishDrawing();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [dispatch, finishDrawing, setDrawing]);

  // Select all overlays with Ctrl+A
  useEffect(() => {
    const selectAll = () => dispatch(setSelectedOverlayIds(overlays.map((overlay) => overlay.id)));
    eventBus.on('select-all', selectAll);
    return () => {
      eventBus.off('select-all', selectAll);
    };
  }, [dispatch, overlays]);

  const mapHandlers = useMemo<LeafletEventHandlerFnMap>(() => ({
    click: (e) => {
      const target = e.originalEvent?.target;
      if (target instanceof Element && target.closest('.overlay-drawing-controls')) return;
      // Clicking outside the selected overlay closes it, before drawing a new one
      if (drawPointsRef.current.length === 0 && selectedOverlayId) {
        dispatch(setSelectedOverlayId(null));
        return;
      }
      if (e.originalEvent?.detail === 2) return;
      const point = toSelectionPoint(e.latlng);
      setDrawing(appendSelectionPoint(drawPointsRef.current, point));
      setCursor(point);
    },
    mousemove: (e) => {
      if (drawPointsRef.current.length === 0) return;
      setCursor(toSelectionPoint(e.latlng));
    },
    contextmenu: (e) => {
      if (drawPointsRef.current.length === 0) return;
      e.originalEvent.preventDefault();
      L.DomEvent.stopPropagation(e);
      finishDrawing();
    },
  }), [dispatch, finishDrawing, selectedOverlayId, setDrawing]);

  const commitPoints = (points: OverlayPoint[]) => {
    if (!selectedOverlay) return;
    setEditPoints(points);
    dispatch(updateOverlayInImage({ id: selectedOverlay.id, changes: { points } }));
  };

  // Moves the whole overlay, with its label
  const moveOverlay = (dx: number, dy: number) => {
    if (!selectedOverlay) return;
    const labelPosition = selectedOverlay.labelId ? labelPositions[selectedOverlay.labelId] : undefined;
    groupedUndo.batch(() => {
      commitPoints(translatePoints(selectedOverlay.points, dx, dy));
      if (selectedOverlay.labelId && labelPosition) {
        dispatch(moveLabelInImage({
          id: selectedOverlay.labelId,
          x: Number(labelPosition.x) + dx,
          y: Number(labelPosition.y) + dy,
        }));
      }
    });
  };

  const removePoint = (index: number) => {
    if (!editPoints || editPoints.length <= MIN_POINTS) return;
    commitPoints(editPoints.filter((_, i) => i !== index));
  };

  // Where the move handle is: by the saved overlay. The same position while it's
  // dragged, as react-leaflet moves markers back to a changed position
  const moveCorner = useMemo(
    () => selectedOverlay && toPositions([topRightCorner(selectedOverlay.points)])[0],
    [selectedOverlay],
  );

  const drawingPreview = cursor && drawPoints.length > 0 ? [...drawPoints, cursor] : drawPoints;

  return (
    <>
      <MapEventHandler enabled={true} handlers={mapHandlers} />

      {drawPoints.map((point, index) => (
        <CircleMarker
          key={`${point.x}-${point.y}-${index}`}
          center={[point.x, point.y]}
          radius={4}
          pathOptions={{ color: '#fff', weight: 2, fillColor: '#168c94', fillOpacity: 1 }}
          interactive={false}
        />
      ))}
      {drawingPreview.length >= MIN_POINTS ? (
        <Polygon
          positions={toPositions(drawingPreview)}
          pathOptions={{ ...PREVIEW_STYLE, fillColor: '#168c94', fillOpacity: 0.2 }}
          interactive={false}
        />
      ) : drawingPreview.length >= 2 ? (
        <Polyline positions={toPositions(drawingPreview)} pathOptions={PREVIEW_STYLE} interactive={false} />
      ) : null}

      {/* Several selected: outlined, reshaped one at a time */}
      {selectedOverlay && otherSelectedOverlays.length > 0 && drawPoints.length === 0
        && [selectedOverlay, ...otherSelectedOverlays].map((overlay) => (
          <Polygon
            key={`outline-${overlay.id}`}
            positions={toPositions(overlay.points)}
            pathOptions={{ ...PREVIEW_STYLE, fillOpacity: 0 }}
            interactive={false}
          />
        ))}

      {selectedOverlay && editPoints && moveCorner && otherSelectedOverlays.length === 0 && drawPoints.length === 0 && (
        <>
          <Polygon
            positions={toPositions(editPoints)}
            pathOptions={{ ...PREVIEW_STYLE, fillOpacity: 0 }}
            interactive={false}
          />
          <LeafletMarker
            position={moveCorner}
            icon={moveIcon}
            draggable={true}
            title="Move overlay"
            eventHandlers={{
              drag: (event) => {
                const { lat, lng } = (event.target as L.Marker).getLatLng();
                setEditPoints(translatePoints(selectedOverlay.points, lat - moveCorner[0], lng - moveCorner[1]));
              },
              dragend: (event) => {
                const { lat, lng } = (event.target as L.Marker).getLatLng();
                moveOverlay(lat - moveCorner[0], lng - moveCorner[1]);
              },
              click: (event) => L.DomEvent.stopPropagation(event),
            }}
          />
          {editPoints.map((point, index) => (
            <LeafletMarker
              key={`vertex-${index}`}
              position={[point.x, point.y]}
              icon={vertexIcon}
              draggable={true}
              title="Drag to move, right-click to remove"
              eventHandlers={{
                drag: (event) => {
                  const { lat, lng } = (event.target as L.Marker).getLatLng();
                  setEditPoints((current) => current && current.map((p, i) => i === index ? { x: lat, y: lng } : p));
                },
                dragend: (event) => {
                  const { lat, lng } = (event.target as L.Marker).getLatLng();
                  commitPoints(editPoints.map((p, i) => i === index ? { x: lat, y: lng } : p));
                },
                contextmenu: (event) => {
                  event.originalEvent.preventDefault();
                  L.DomEvent.stopPropagation(event);
                  removePoint(index);
                },
                click: (event) => {
                  L.DomEvent.stopPropagation(event);
                  if (event.originalEvent.altKey) removePoint(index);
                },
              }}
            />
          ))}
          {editPoints.map((point, index) => {
            const next = editPoints[(index + 1) % editPoints.length];
            const midpoint = { x: (point.x + next.x) / 2, y: (point.y + next.y) / 2 };
            return (
              <LeafletMarker
                key={`midpoint-${index}`}
                position={[midpoint.x, midpoint.y]}
                icon={midpointIcon}
                title="Click to add a point"
                eventHandlers={{
                  click: (event) => {
                    L.DomEvent.stopPropagation(event);
                    commitPoints([...editPoints.slice(0, index + 1), midpoint, ...editPoints.slice(index + 1)]);
                  },
                }}
              />
            );
          })}
        </>
      )}

      {drawPoints.length > 0 && createPortal(
        <div className="overlay-drawing-controls">
          <button
            type="button"
            disabled={drawPoints.length < MIN_POINTS}
            onClick={(event) => {
              event.stopPropagation();
              finishDrawing();
            }}
          >
            Finish overlay
          </button>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setDrawing([]);
            }}
          >
            Cancel
          </button>
        </div>,
        mapContainer,
      )}

      {selectedOverlay && createPortal(
        <EditOverlay
          key={selectedOverlay.id}
          overlay={selectedOverlay}
          otherOverlays={otherSelectedOverlays}
          onPreviewChange={onStylePreviewChange}
          newLabelPosition={newLabelPosition}
          onClose={() => dispatch(setSelectedOverlayId(null))}
        />,
        rootElement || document.body,
      )}
    </>
  );
}
